// src/store/slices/authSlice.ts

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AdminLoginRequest, UserInfo } from "../../types";
import { loginAdmin } from "../../api/authApi";
import {
  clearSession,
  REFRESH_KEY,
  TOKEN_KEY,
  USER_KEY,
} from "../../api/client";
import { getErrorMessage } from "../../utils/errors";

// ─── Estado ───────────────────────────────────────────────────────────────────

interface AuthState {
  token: string | null;
  user: UserInfo | null;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  token: localStorage.getItem(TOKEN_KEY),
  user: (() => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || "null");
    } catch {
      return null;
    }
  })(),
  loading: false,
  error: null,
};

// ─── Thunk de login ──────────────────────────────────────────────────────────

export const loginThunk = createAsyncThunk(
  "auth/adminLogin",
  async (payload: AdminLoginRequest, { rejectWithValue }) => {
    try {
      return await loginAdmin(payload);
    } catch (err: any) {
      // 401 → "Usuario o contraseña incorrectos."
      // 403 → "No tienes permisos para acceder al Panel Administrativo."
      return rejectWithValue(
        getErrorMessage(
          err,
          "Error al iniciar sesión. Verifica tus credenciales.",
        ),
      );
    }
  },
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.token = null;
      state.user = null;
      clearSession();
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        const p = action.payload;
        state.loading = false;
        state.token = p.token;
        state.user = {
          userId: p.userId,
          email: p.email,
          username: p.username,
          firstName: p.firstName,
          lastName: p.lastName,
          imagePath: p.imagePath,
          roles: p.roles,
        };
        localStorage.setItem(TOKEN_KEY, p.token);
        localStorage.setItem(REFRESH_KEY, p.refreshToken);
        localStorage.setItem(USER_KEY, JSON.stringify(state.user));
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
